import React from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Autocomplete from '@material-ui/lab/Autocomplete';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { OnwardChip, PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { Body1 } from '../../blocks';
import { CheckboxDescription, SectionHeading } from '../blocks';

const AddSubscriptionModal = ({ addModal, setAddModal, commSections, loading, onConfirm }) => {
    const selectedCommunicationIds = addModal?.selectedCommunicationIds || [];
    const emails = addModal?.emails || [];
    const close = () => setAddModal(null);

    // Confirm stays enabled once the user has entered anything, so onConfirm can explain what is
    // missing. Disabling it on "no valid emails" left an all-invalid entry with no feedback at all.
    const dirty = selectedCommunicationIds.length > 0 || emails.length > 0;

    const toggleCommunication = (communication_id, checked) => {
        setAddModal({
            ...addModal,
            selectedCommunicationIds: checked
                ? [...selectedCommunicationIds, communication_id]
                : selectedCommunicationIds.filter((id) => id !== communication_id),
        });
    };

    return (
        <NavResponsiveModal open={!!addModal} onClose={close}>
            <ModalHeader title="Choose New Email Subscriptions" onClose={close} />
            <ModalContent
                css={css`
                    width: 600px;
                `}
            >
                <Body1
                    css={css`
                        margin-bottom: 1rem;
                    `}
                >
                    Choose one or more communications to subscribe to and then add email addresses at the bottom:
                </Body1>
                <Grid container direction="column">
                    {commSections.map((section) => (
                        <div
                            key={section.section}
                            css={css`
                                margin-bottom: 0.75rem;
                            `}
                        >
                            <SectionHeading
                                css={css`
                                    display: block;
                                    margin-top: 0.75rem;
                                    margin-bottom: 0.25rem;
                                `}
                            >
                                {section.section}
                            </SectionHeading>
                            {section.options.map((comm) => (
                                <Grid item key={comm.communication_id}>
                                    <div
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                        `}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedCommunicationIds.includes(comm.communication_id)}
                                                    onChange={(e) =>
                                                        toggleCommunication(comm.communication_id, e.target.checked)
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={<Body1>{comm.name || comm.label}</Body1>}
                                            css={css`
                                                margin-left: -8px;
                                                margin-right: 0;
                                            `}
                                        />
                                        {comm.description && (
                                            <CheckboxDescription>{comm.description}</CheckboxDescription>
                                        )}
                                    </div>
                                </Grid>
                            ))}
                        </div>
                    ))}
                </Grid>

                <Body1
                    css={css`
                        margin-top: 3.5rem;
                        margin-bottom: 0.5rem;
                    `}
                >
                    Add the email addresses to CC on these notifications:
                </Body1>
                <Autocomplete
                    id="add-subscription-emails-autocomplete"
                    disabled={loading}
                    multiple
                    fullWidth
                    freeSolo
                    clearOnBlur
                    value={emails}
                    options={[]}
                    filterOptions={(options, params) =>
                        params.inputValue === ''
                            ? []
                            : [{ email: params.inputValue, title: `Add "${params.inputValue}"` }]
                    }
                    getOptionSelected={(option, value) => option?.email === value || option === value}
                    getOptionLabel={(option) =>
                        typeof option === 'string' ? option : option?.title || option?.email || ''
                    }
                    renderInput={(params) => <TextField {...params} label="Add Emails" />}
                    renderTags={(options, getTagProps) =>
                        options.map((option, index) => (
                            <OnwardChip
                                key={typeof option === 'string' ? option : option?.email}
                                disabled={loading}
                                label={typeof option === 'string' ? option : option?.email}
                                {...getTagProps({ index })}
                                onDelete={() =>
                                    setAddModal({
                                        ...addModal,
                                        emails: emails.filter((_, i) => i !== index),
                                    })
                                }
                            />
                        ))
                    }
                    onChange={(e, value = []) =>
                        setAddModal({
                            ...addModal,
                            emails: value.map((option) => (typeof option === 'string' ? option : option?.email)),
                        })
                    }
                />
            </ModalContent>
            <ModalActions
                css={css`
                    margin-top: 1.5rem;
                `}
            >
                <SecondaryButton onClick={close}>Cancel</SecondaryButton>
                <PrimaryButton onClick={onConfirm} disabled={loading || !dirty}>
                    Confirm
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default AddSubscriptionModal;
